import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Home from './components/Home';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp'
import ChangePassword from './components/auth/ChangePassword'
import { ThemeProvider, createMuiTheme, makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { AuthContext } from "./context/auth";
import { MessageContext } from "./context/message";
import { ChatContext } from './context/chat'
import { FieldContext } from './context/context'
import { RackPricingContext } from './context/rackPricing'
import PrivateRoute from './routes/PrivateRoute';
import {
  BrowserRouter as Router,
  Switch,
  Route,
} from "react-router-dom";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const primary = process.env.NODE_ENV === 'production' ? '#fc6c05' : '#1EFF00'
const RACK_PRICING_STORAGE_KEY = 'mills_pricing';
const LEGACY_RACK_PRICING_PREFIXES = [
  'rackPricingV5',
  'rackPricingV3:',
  'rackPricingHistoryV4:',
  'rackPricingAccessV1:',
  'millsPricingAccess',
];

const removeLegacyRackPricingStorage = () => {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key && LEGACY_RACK_PRICING_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      sessionStorage.removeItem(key);
    }
  }
};

const readRackPricingStorage = () => {
  removeLegacyRackPricingStorage();

  try {
    const stored = JSON.parse(sessionStorage.getItem(RACK_PRICING_STORAGE_KEY) || 'null');
    if (stored) {
      return {
        current: stored.current || {},
        history: stored.history || {},
        access: stored.access ?? null,
      };
    }
  } catch (error) {
    // Replace malformed or outdated browser storage with an empty cache.
  }

  sessionStorage.removeItem(RACK_PRICING_STORAGE_KEY);
  return { current: {}, history: {}, access: null };
};

const writeRackPricingStorage = (current, history, access) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(RACK_PRICING_STORAGE_KEY) || 'null');
    sessionStorage.setItem(RACK_PRICING_STORAGE_KEY, JSON.stringify({
      current,
      history,
      access: access ?? stored?.access ?? null,
    }));
  } catch (storageError) {
    // Keep the response in memory when browser storage capacity is exceeded.
  }
};

const theme = createMuiTheme({
  palette: {
    type: 'dark',
    primary: {
      main: primary,
    },
    secondary: {
      main: '#FFFFFF',
      secondary: '#545454',
    },
  },
});

const useStyles = makeStyles((theme) => ({
  '@global': {
    /* width */
    '::-webkit-scrollbar': {
      height: '8px',
      width: '8px'
    },
    /* Handle */
    '::-webkit-scrollbar-thumb' :{
      background: '#111',
      'borderRadius':'25px',
      border: '2px solid white'

    },
    /* Handle on hover */
    '::-webkit-scrollbar-thumb:hover' :{
      background: '#555'
    },
  },
  root: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
}));

function App() {
  const classes = useStyles();
  const initialRackPricingStorageRef = useRef(null)
  if (!initialRackPricingStorageRef.current) {
    initialRackPricingStorageRef.current = readRackPricingStorage()
  }
  const initialRackPricingStorage = initialRackPricingStorageRef.current
  const [message, setMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(sessionStorage.getItem('token'));
  const [username, setUsername] = useState(sessionStorage.getItem('username'));
  const [userCategory, setUserCategory] = useState(sessionStorage.getItem('category'));
  const [user_id, setUser_id] = useState(sessionStorage.getItem('user_id'))
  const [team_id, setTeam_id] = useState(sessionStorage.getItem('team_id'))
  const [tutorialStep, setTutorialStep] = useState(Number(sessionStorage.getItem('tutorialStep')))
  const [dataSub, setDataSub] = useState(Number(sessionStorage.getItem('dataSub')))
  const [alphabbl, setAlphabbl] = useState(Number(sessionStorage.getItem('alphabbl')))
  const [tankwatch_api, setTankwatch] = useState(sessionStorage.getItem('tankwatch_api'))
  const [overlayAccess, setOverlayAccess]  = useState(sessionStorage.getItem('overlayAccess'))
  const [extendedOverlayAccess, setExtendedOverlayAccess] = useState(sessionStorage.getItem('extendedOverlayAccess'))
  const [millsPricingAccess, setMillsPricingAccess] = useState(Number(initialRackPricingStorage.access))
  const [openConversations, setOpenConversations] = useState([])
  const [currentChat, setCurrentChat] = useState({})
  const [chatroom, setChatroom] = useState(false)
  let anchorRef = useRef([])
  const [fetchConversations, setFetchConversations] = useState(false)
  const [expired, setExpired] = useState(false)
  //field context
  const [regions, setRegions] = useState([])
  const [product_categories, setProduct_categories] = useState([])
  const [products, setProducts] = useState([])
  const [fetchFields, setFetchFields] = useState(false)
  const [rackPricingCache, setRackPricingCache] = useState(initialRackPricingStorage.current)
  const rackPricingCacheRef = useRef(rackPricingCache)
  const rackPricingRequests = useRef({})
  const [rackPricingHistoryCache, setRackPricingHistoryCache] = useState(initialRackPricingStorage.history)
  const rackPricingHistoryCacheRef = useRef(rackPricingHistoryCache)
  const rackPricingHistoryRequests = useRef({})

  const loadRackPricing = useCallback((timeCode = 'TR', force = false) => {
    const normalizedTimeCode = timeCode.toUpperCase();
    const cached = rackPricingCacheRef.current[normalizedTimeCode];
    if (!force && cached && cached.data) {
      return Promise.resolve(cached.data);
    }

    if (rackPricingRequests.current[normalizedTimeCode]) {
      return rackPricingRequests.current[normalizedTimeCode];
    }

    const pricingRequest = fetch(`/api/mills-price/current?timeCode=${normalizedTimeCode}`, {
      method: 'GET',
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load rack pricing (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        const nextEntry = { data };
        setRackPricingCache((previous) => {
          const nextCache = { ...previous, [normalizedTimeCode]: nextEntry };
          rackPricingCacheRef.current = nextCache;
          writeRackPricingStorage(nextCache, rackPricingHistoryCacheRef.current);
          return nextCache;
        });
        return data;
      })
      .finally(() => {
        delete rackPricingRequests.current[normalizedTimeCode];
      });

    rackPricingRequests.current[normalizedTimeCode] = pricingRequest;
    return pricingRequest;
  }, []);

  const loadRackPriceHistory = useCallback((force = false) => {
    const cacheId = 'ALL';
    const cached = rackPricingHistoryCacheRef.current[cacheId];
    if (!force && cached && cached.data) {
      return Promise.resolve(cached.data);
    }
    if (rackPricingHistoryRequests.current[cacheId]) {
      return rackPricingHistoryRequests.current[cacheId];
    }

    const historyRequest = fetch('/api/mills-price/history/all', {
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message?.text || 'Unable to load rack price history.');
        return data;
      })
      .then((data) => {
        const nextEntry = { data };
        setRackPricingHistoryCache((previous) => {
          const nextCache = { ...previous, [cacheId]: nextEntry };
          rackPricingHistoryCacheRef.current = nextCache;
          writeRackPricingStorage(rackPricingCacheRef.current, nextCache);
          return nextCache;
        });
        return data;
      })
      .finally(() => {
        delete rackPricingHistoryRequests.current[cacheId];
      });

    rackPricingHistoryRequests.current[cacheId] = historyRequest;
    return historyRequest;
  }, []);

  const refreshRackPricingAccess = useCallback((targetUserId, hasExpandedAccess) => {
    const activeUserId = sessionStorage.getItem('user_id');
    if (String(targetUserId) !== String(activeUserId)) return Promise.resolve();

    sessionStorage.removeItem(RACK_PRICING_STORAGE_KEY);
    rackPricingCacheRef.current = {};
    rackPricingHistoryCacheRef.current = {};
    setRackPricingCache({});
    setRackPricingHistoryCache({});
    const nextMillsPricingAccess = hasExpandedAccess ? 1 : 0;
    writeRackPricingStorage({}, {}, nextMillsPricingAccess);
    setMillsPricingAccess(nextMillsPricingAccess);

    return Promise.all([
      loadRackPricing('TR', true),
      loadRackPricing('TE', true),
      loadRackPriceHistory(true),
    ]);
  }, [loadRackPriceHistory, loadRackPricing]);

  useEffect(() => {
    const nextRackPricingStorage = readRackPricingStorage();
    const nextPricingCache = nextRackPricingStorage.current;
    const nextHistoryCache = nextRackPricingStorage.history;
    rackPricingCacheRef.current = nextPricingCache;
    rackPricingHistoryCacheRef.current = nextHistoryCache;
    rackPricingRequests.current = {};
    rackPricingHistoryRequests.current = {};
    setRackPricingCache(nextPricingCache);
    setRackPricingHistoryCache(nextHistoryCache);
    setMillsPricingAccess(Number(nextRackPricingStorage.access));
  }, [user_id]);

  const auth = (response) => {
    if (response.status === 401 && isAuthenticated) {
      setExpired(true)
    }
    if (response.status === 413){
      return {'message': {'type': 'error', 'text': 'Unable to attach files. Upload size too large.'}}
    }
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      return response.json()
    }

    return response.text().then((text) => {
      try {
        return JSON.parse(text);
      } catch (error) {
        return {
          message: {
            type: 'error',
            text: response.ok
              ? 'Unexpected server response.'
              : `Server returned ${response.status}: ${text.slice(0, 120) || response.statusText}`
          }
        };
      }
    })
  };

  useEffect(() => {
    fetch('/api/auth', {
      method: 'GET',
      headers: {
        'x-access-token': sessionStorage.getItem('token')
      }
    })
    .then(response => auth(response))
    .then((data) => {
      if (data.isAuthenticated) {
        setIsAuthenticated(data.isAuthenticated);
        setUsername(data.username);
        setUserCategory(data.category);
        setUser_id(data.user_id);
        setTeam_id(data.team_id);
        setDataSub(data.data_sub)
        setAlphabbl(data.alphabbl)
        setTankwatch(data.tankwatch_api)
        if (!tutorialStep && tutorialStep !== 0)
        {
            setTutorialStep(data.tutorialStep)
        }
        setOverlayAccess(data.overlayAccess)
        setExtendedOverlayAccess(data.extendedOverlayAccess)
        setMillsPricingAccess(data.millsPricingAccess)
        writeRackPricingStorage(
          rackPricingCacheRef.current,
          rackPricingHistoryCacheRef.current,
          data.millsPricingAccess
        )
        setMessage(data.message);
        setExpired(false);
      } else {
        setIsAuthenticated(data.isAuthenticated);
        setMessage(data.message);
      }
    })
    .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(() => {
    fetch('/api/context', {
      method: 'GET',
      headers: {
        'x-access-token': sessionStorage.getItem('token')
      }
    })
    .then(response => auth(response))
    .then((response) => {
      setRegions(response.regions)
      setProduct_categories(response.product_categories)
      setProducts(response.products)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[fetchFields])

  useEffect(() => {
    if (tutorialStep || tutorialStep === 0)
      sessionStorage.setItem('tutorialStep', tutorialStep)
  }, [tutorialStep])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setMessage(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        expired,
        setExpired,
        auth,
        username,
        setUsername,
        userCategory,
        setUserCategory,
        user_id,
        setUser_id,
        team_id,
        setTeam_id,
        tutorialStep,
        setTutorialStep,
        dataSub,
        setDataSub,
        alphabbl,
        setAlphabbl,
        tankwatch_api,
        setTankwatch,
        overlayAccess,
        setOverlayAccess,
        extendedOverlayAccess,
        setExtendedOverlayAccess,
        millsPricingAccess,
        setMillsPricingAccess
      }}>
      <FieldContext.Provider
        value = {{
          regions,
          product_categories,
          products,
          fetchFields,
          setFetchFields
      }}>
        <RackPricingContext.Provider
          value={{
            rackPricingCache,
            loadRackPricing,
            rackPricingHistoryCache,
            loadRackPriceHistory,
            refreshRackPricingAccess,
          }}>
          <MessageContext.Provider
            value={{
              message,
              setMessage
            }}>
          <ChatContext.Provider
            value = {{
              openConversations,
              setOpenConversations,
              currentChat,
              setCurrentChat,
              chatroom,
              setChatroom,
              fetchConversations,
              setFetchConversations,
              anchorRef
            }}>
            <ThemeProvider theme={theme}>
              <Router>

                <Switch>
                  <Route path='/login' component={Login} />
                  <Route path = '/changePassword/:token' component={ChangePassword}/>
                  <Route path = {`/sign_up`} component={SignUp}/>
                  <PrivateRoute path='/' component={Home} />
                </Switch>

                <Snackbar autoHideDuration={6000} className={classes.root} open={Boolean(message)} onClose={handleClose} style = {tutorialStep !== 0 ? {bottom:85} :null}>
                  {message && <Alert onClose={handleClose} severity={message.type}>{message.text}</Alert>}
                </Snackbar>
              </Router>
            </ThemeProvider>
          </ChatContext.Provider>
          </MessageContext.Provider>
        </RackPricingContext.Provider>
      </FieldContext.Provider>
    </AuthContext.Provider>
  );

}

export default App;
